// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import { ApiBase, type ApiRequestConfig } from './apiBase';
import type { ServiceConfig } from '@/types';
import type { ExternalServicesConfig } from '@/types/externalServices';
import type {
  ModelConfigPublicResponse,
  ModelConfigTestResponse,
  ProviderModelConfigFiveTuple,
} from '@/types/modelConfig';
import type {
  AuditEvent,
  ConfigVersionRecord,
  ModelCatalogResponse,
  ModelTiersConfig,
  NodeModelBinding,
  NodeModelBindingsConfig,
  SettingsModulesConfig,
  WorkflowMetadata,
} from '@/types/workflowConfig';
import type { McpCatalogResponse, SkillExecuteResponse, SkillsCatalogResponse } from '@/types';
import type { ModelProviderCatalogResponse } from '@/types/modelProviderCatalog';
import type {
  ModelRegistryCatalogResponse,
  ModelRegistryCompareResponse,
  ModelRegistryGuidesResponse,
} from '@/types/modelRegistry';
import type {
  ModelDeploymentTestPayload,
  ModelDeploymentTestResponse,
  ModelSettingsDocument,
  ModelSettingsWritePayload,
} from '@/types/modelSettingsV3';

export class ApiConfig extends ApiBase {
  // Config
  async getConfig() {
    return this.client.get('/v1/config/services');
  }

  async updateConfig(config: ServiceConfig) {
    return this.client.put('/v1/config/services', config);
  }

  /**
   * external-services（模型/外部服务）配置
   * - GET/PUT 由 api-gateway 提供：/api/v1/config/external-services
   */
  async getExternalServicesConfig() {
    return this.client.get('/v1/config/external-services');
  }

  async updateExternalServicesConfig(config: ExternalServicesConfig) {
    return this.client.put('/v1/config/external-services', config);
  }

  /**
   * model-config（工作流编辑器左侧"模型面板"使用）
   *
   * 路由说明：
   * - 由 api-gateway 提供：/api/model-config（注意：不在 /v1 前缀下）
   * - GET 返回脱敏 api_key（has_api_key/api_key_last4），前端需做"api_key 重填/沿用"逻辑
   */
  async listModelConfigs() {
    // G7-M: GET /model-config 是 admin 守卫面，普通用户（KB 配置表单模型下拉）403
    // 是预期路径——全局 toast 静默，错误状态由 store.error 承载（管理员中心 UI 呈现）。
    return this.client.get<ModelConfigPublicResponse>('/model-config', {
      suppressUnauthorizedRedirect: true,
      suppressErrorToast: true,
    } as ApiRequestConfig);
  }

  /**
   * Upsert 单个 provider_code 的配置
   * - 后端支持 api_key 为空/缺失 => 沿用旧值
   */
  async upsertModelConfig(
    payload: {
      provider_code: string;
      config: ProviderModelConfigFiveTuple;
    },
    // G7-L: 迁移路径对非 admin 是预期 403，需要静默容忍（不弹全局 toast）
    options?: { suppressErrorToast?: boolean }
  ) {
    return this.client.put<ModelConfigPublicResponse>('/model-config', payload, {
      suppressUnauthorizedRedirect: true,
      suppressErrorToast: options?.suppressErrorToast,
    } as ApiRequestConfig);
  }

  async deleteModelConfig(provider_code: string) {
    return this.client.delete<ModelConfigPublicResponse>('/model-config', {
      params: { provider_code },
      suppressUnauthorizedRedirect: true,
    } as ApiRequestConfig);
  }

  /**
   * 一键测试连通性
   * - 成功返回 20 字摘要与 tested_ok
   */
  async testModelConfig(payload: { provider_code: string; config: ProviderModelConfigFiveTuple }) {
    return this.client.post<ModelConfigTestResponse>('/model-config/test', payload, {
      suppressUnauthorizedRedirect: true,
    } as ApiRequestConfig);
  }

  async getModelSettingsV3() {
    return this.client.get<ModelSettingsDocument>('/model-settings', {
      suppressUnauthorizedRedirect: true,
    } as ApiRequestConfig);
  }

  async updateModelSettingsV3(payload: ModelSettingsWritePayload) {
    return this.client.put<ModelSettingsDocument>('/model-settings', payload, {
      suppressUnauthorizedRedirect: true,
    } as ApiRequestConfig);
  }

  async testModelSettingsDeployment(
    deploymentId: string,
    payload: ModelDeploymentTestPayload
  ) {
    return this.client.post<ModelDeploymentTestResponse>(
      `/model-settings/deployments/${encodeURIComponent(deploymentId)}/test`,
      payload,
      { suppressUnauthorizedRedirect: true } as ApiRequestConfig
    );
  }

  async getModelProviderCatalog() {
    return this.client.get<ModelProviderCatalogResponse>('/model-providers/catalog');
  }

  async getModelRegistryCatalog(params?: {
    q?: string;
    provider?: string;
    category?: string;
    tag?: string;
    capability?: string;
    local_only?: boolean;
    tested_only?: boolean;
    limit?: number;
    offset?: number;
  }) {
    return this.client.get<ModelRegistryCatalogResponse>('/model-registry/catalog', { params });
  }

  async refreshModelRegistry(payload?: { providers?: string[]; force?: boolean }) {
    return this.client.post('/model-registry/refresh', payload || {});
  }

  async getModelRegistryRefreshStatus() {
    return this.client.get('/model-registry/refresh/status');
  }

  async getModelRegistryProviderModels(
    providerCode: string,
    params?: {
      q?: string;
      category?: string;
      tag?: string;
      capability?: string;
      limit?: number;
      offset?: number;
    }
  ) {
    return this.client.get<ModelRegistryCatalogResponse>(
      `/model-registry/providers/${encodeURIComponent(providerCode)}/models`,
      { params }
    );
  }

  async compareModelRegistryModels(payload: { model_ids: string[]; profile?: string }) {
    return this.client.post<ModelRegistryCompareResponse>('/model-registry/compare', payload);
  }

  async getModelRegistryGuides() {
    return this.client.get<ModelRegistryGuidesResponse>('/model-registry/guides');
  }

  // Modular settings
  async getSettingsModules() {
    return this.client.get<SettingsModulesConfig>('/v1/config/settings/modules');
  }

  async validateSettingsModules(config: SettingsModulesConfig) {
    return this.client.post('/v1/config/settings/modules/validate', config);
  }

  async updateSettingsModules(config: SettingsModulesConfig) {
    return this.client.put<SettingsModulesConfig>('/v1/config/settings/modules', config);
  }

  async getSkillsCatalog(params?: { q?: string; limit?: number }) {
    return this.client.get<SkillsCatalogResponse>('/v1/skills/catalog', { params });
  }

  async getSkillDetail(skillId: string) {
    return this.client.get(`/v1/skills/${encodeURIComponent(skillId)}`);
  }

  async reloadSkillsCatalog() {
    return this.client.post('/v1/skills/reload', {});
  }

  async executeSkills(payload: {
    user_query: string;
    skill_ids: string[];
    actor_mode?: 'teacher' | 'student';
    teacher_content_type?: string;
    class_context?: Record<string, unknown>;
  }) {
    return this.client.post<SkillExecuteResponse>('/v1/skills/execute', payload);
  }

  async getMcpCatalog() {
    return this.client.get<McpCatalogResponse>('/v1/mcp/catalog');
  }

  async reloadMcpCatalog() {
    return this.client.post('/v1/mcp/reload', {});
  }

  // Model catalog and node bindings
  async getModelCatalog(filters?: {
    model_type?: string;
    performance?: string;
    cost?: string;
    region?: string;
    capability?: 'chat' | 'retrieval';
  }) {
    return this.client.get<ModelCatalogResponse>('/v1/config/model-catalog', {
      params: filters,
    });
  }

  async getNodeModelBindings() {
    return this.client.get<NodeModelBindingsConfig>('/v1/config/node-model-bindings');
  }

  async validateNodeModelBindings(config: NodeModelBindingsConfig) {
    return this.client.post('/v1/config/node-model-bindings/validate', config);
  }

  async batchUpdateNodeModelBindings(payload: { updates: NodeModelBinding[]; comment?: string }) {
    return this.client.post<NodeModelBindingsConfig>(
      '/v1/config/node-model-bindings/batch',
      payload
    );
  }

  // Model Tiers (new tier-based API)
  async getModelTiers() {
    return this.client.get<ModelTiersConfig>('/v1/config/model-tiers');
  }

  async saveModelTiers(config: ModelTiersConfig) {
    return this.client.put<ModelTiersConfig>('/v1/config/model-tiers', config);
  }

  async validateModelTiers(config: ModelTiersConfig) {
    return this.client.post('/v1/config/model-tiers/validate', config);
  }

  // Versioning and governance
  async getConfigVersions(params?: { module?: string; limit?: number }) {
    return this.client.get<{ items: ConfigVersionRecord[] }>('/v1/config/versions', { params });
  }

  async rollbackConfig(payload: { module: string; version_id: string; comment?: string }) {
    return this.client.post('/v1/config/rollback', payload);
  }

  async exportConfigBundle() {
    return this.client.get('/v1/config/export');
  }

  async importConfigBundle(payload: {
    settings_modules?: SettingsModulesConfig;
    model_catalog?: ModelCatalogResponse;
    node_model_bindings?: NodeModelBindingsConfig;
    validate_only?: boolean;
  }) {
    return this.client.post('/v1/config/import', payload);
  }

  async getAuditLogs(params?: { module?: string; limit?: number }) {
    return this.client.get<{ items: AuditEvent[] }>('/v1/config/audit/logs', { params });
  }

  async validateProviderKeys(payload?: { profiles?: string[]; check_connectivity?: boolean }) {
    return this.client.post('/v1/config/providers/validate-keys', payload || {});
  }

  // Workflow metadata
  async getWorkflowMetadata() {
    return this.client.get<WorkflowMetadata>('/v1/workflow/metadata');
  }

  async refreshWorkflowScan() {
    return this.client.post('/v1/workflow/scan/refresh');
  }
}
